import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientTranslation } from '@/lib/core';
import { useApp } from '@/lib/core/providers/appProvider';
import { useLocaleStore, useRecentSearchesStore } from '@/lib/core/stores';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { useEventsByDay, useEventFavoriteIndex } from '@/services/platform/data/events';
import type { EventListItemDto } from '@/services/platform/api/model';
import { EventCard } from '../list/EventCard';
import { DateDivider } from '../list/DateDivider';
import { SoldOutEventSheet } from '../list/SoldOutEventSheet';
import { formatDayGroupLabel } from '../list/format';
import { isMembersOnly } from '../list/visibility';
import { isCardSoldOut } from '../rules';

/** `useRecentSearchesStore` namespace for Events (the store is generic, keyed by an arbitrary section string). */
const RECENT_SEARCHES_SECTION = 'events';
const DEBOUNCE_MS = 500;
/** Stable fallback reference — an inline `?? []` would create a new array every render and loop `useSyncExternalStore`. */
const EMPTY_TERMS: string[] = [];

export interface EventsSearchOverlayProps {
  open: boolean;
  onClose: () => void;
  /** Scopes the search to the ELP's currently selected hub (undefined = all hubs). */
  hubKey?: string;
}

/**
 * Members-only Events search — a self-contained overlay (no CMS/routing
 * dependency), modeled on `BenefitsSearchOverlay`. Unlike that screen, Events
 * has a single cheap `q` search (no separate autocomplete endpoint), so results
 * update live from one debounced call rather than needing a distinct
 * typing/submit step — the "complete searches" row surfaces matching recent
 * search terms client-side instead of a live suggestions API.
 */
export function EventsSearchOverlay({ open, onClose, hubKey }: EventsSearchOverlayProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const { appSettings, user } = useApp();
  const eventDetailPath = appSettings?.properties?.eventDetail?.route?.path;
  const isMember = !!user?.hasActiveMembership;
  const locale = useLocaleStore(state => state.locale);
  const recentTerms = useRecentSearchesStore(
    state => state.termsBySection[RECENT_SEARCHES_SECTION] ?? EMPTY_TERMS
  );
  const addRecentTerm = useRecentSearchesStore(state => state.addTerm);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setDebouncedQuery('');
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(focusTimer);
  }, [open]);

  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setDebouncedQuery('');
      return;
    }
    debounceRef.current = setTimeout(() => setDebouncedQuery(value.trim()), DEBOUNCE_MS);
  }, []);

  const handleSelectTerm = useCallback(
    (term: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setQuery(term);
      setDebouncedQuery(term);
      addRecentTerm(RECENT_SEARCHES_SECTION, term);
    },
    [addRecentTerm]
  );

  const handleClose = useCallback(() => {
    const trimmed = query.trim();
    if (trimmed) addRecentTerm(RECENT_SEARCHES_SECTION, trimmed);
    onClose();
  }, [query, addRecentTerm, onClose]);

  const handleSearchAgain = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const hasQuery = query.trim().length > 0;
  const matchingTerms = useMemo(() => {
    if (!hasQuery) return [];
    const needle = query.trim().toLowerCase();
    return recentTerms.filter(term => term.toLowerCase().includes(needle) && term.toLowerCase() !== needle);
  }, [recentTerms, query, hasQuery]);

  // Grouped like the ELP list (`useEventsByDay`): one request, dated events and campaigns alike,
  // each under every day it happens on at that day's hour.
  const eventsParams = useMemo(() => ({ hubKey, q: debouncedQuery || undefined }), [hubKey, debouncedQuery]);
  const { days: groupedDays, events, isLoading } = useEventsByDay(eventsParams);
  const {
    eventFavoriteLists,
    favoriteListGuidByEventId,
    refetch: refetchEventFavorites,
  } = useEventFavoriteIndex();

  const isSearching = !!debouncedQuery;
  const isLoadingResults = isSearching && isLoading;
  // Distinct events, not cards: a campaign listed under several days is one result.
  const totalResults = isSearching ? events.length : 0;

  const [soldOutEvent, setSoldOutEvent] = useState<EventListItemDto | undefined>(undefined);
  // Mirrors the ELP's `handleSelectEvent` gating: sold-out (capacity) first, then the
  // members-only access gate — without the second check a restricted card reached through
  // search would navigate straight to a detail screen the API then 404s.
  const handleSelectEvent = useCallback(
    (event: EventListItemDto, dayKey?: string) => {
      if (isCardSoldOut(event)) {
        setSoldOutEvent(event);
        return;
      }
      if (!isMember && isMembersOnly(event.visibility)) return;
      // The day it was found under goes along, as from the ELP — the book flow starts on it.
      if (event.id) navigate(`${eventDetailPath}/${event.id}`, { state: { selectedDate: dayKey } });
    },
    [isMember, navigate, eventDetailPath]
  );

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-60 flex flex-col bg-white" role="dialog" aria-modal="true">
        <div
          className="flex shrink-0 items-center gap-2 px-2"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        >
          <button
            type="button"
            aria-label={t('App.Labels.Close')}
            className="flex size-11 shrink-0 items-center justify-center"
            onClick={handleClose}
          >
            <Icon name="xmark" width={24} height={24} className="text-jncquoi-neutral-1000" />
          </button>
          {hasQuery && (
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex-1 text-[14px] leading-none tracking-[0.5px] uppercase">
              {query}
            </p>
          )}
        </div>

        {hasQuery && isSearching && (
          <div className="border-jncquoi-neutral-300 flex shrink-0 items-center border-b-[0.5px] px-4 py-2">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] tracking-[0.5px] uppercase">
              {t('App.Events.SearchResultsCount', { count: totalResults })}
            </p>
          </div>
        )}

        <div className="shrink-0 px-4 pt-2">
          <div className="border-jncquoi-neutral-400 flex items-center border-b py-2">
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={e => handleQueryChange(e.target.value)}
              placeholder={t('App.Events.SearchPlaceholder')}
              className="font-pp-neue-montreal text-jncquoi-neutral-1000 placeholder:text-jncquoi-neutral-700 w-full bg-transparent text-[14px] tracking-[0.5px] outline-none"
              aria-label={t('App.Events.SearchPlaceholder')}
            />
          </div>
        </div>

        <div className="flex-1 scrollbar-none overflow-y-auto px-4">
          {!hasQuery && (
            <div className="flex flex-col gap-8 pt-8">
              {recentTerms.length > 0 && (
                <div className="flex flex-col">
                  <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 py-4 text-[12px] tracking-[0.5px] uppercase">
                    {t('App.Events.SearchSuggestionsTitle')}
                  </p>
                  <div className="flex scrollbar-none gap-6 overflow-x-auto py-2">
                    {recentTerms.map(term => (
                      <button
                        key={term}
                        type="button"
                        onClick={() => handleSelectTerm(term)}
                        className="font-pp-neue-montreal text-jncquoi-neutral-700 shrink-0 text-[14px] tracking-[0.5px] whitespace-nowrap"
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col items-center gap-2 py-8 text-center">
                <div className="flex size-11 items-center justify-center">
                  <Icon name="search" width={20} height={20} className="text-jncquoi-neutral-1000" />
                </div>
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
                  {t('App.Events.SearchHintTitle')}
                </p>
                <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[14px] tracking-[0.5px]">
                  {t('App.Events.SearchHintSubtitle')}
                </p>
              </div>
            </div>
          )}

          {hasQuery && !isSearching && matchingTerms.length > 0 && (
            <div className="flex flex-col pt-4">
              {matchingTerms.map(term => (
                <button
                  key={term}
                  type="button"
                  onClick={() => handleSelectTerm(term)}
                  className="font-pp-neue-montreal text-jncquoi-neutral-1000 py-4 text-left text-[14px] tracking-[0.5px]"
                >
                  <HighlightMatch text={term} query={query} />
                </button>
              ))}
            </div>
          )}

          {isSearching && isLoadingResults && <SpinLoading wrapperClassName="min-h-40" />}

          {isSearching && !isLoadingResults && totalResults > 0 && (
            <div className="flex flex-col gap-8 pt-4 pb-24">
              {groupedDays.map(([key, items]) => (
                <section key={key} className="flex flex-col gap-4">
                  <DateDivider label={formatDayGroupLabel(key, locale)} />
                  <div className="flex flex-col gap-4">
                    {items.map(event => (
                      <EventCard
                        // Keyed by its slot too: the same event can be listed twice under one day.
                        key={`${event.id}-${event.startDateTime}`}
                        event={event}
                        onSelect={selected => handleSelectEvent(selected, key)}
                        favoriteLists={eventFavoriteLists}
                        refetchFavoriteLists={refetchEventFavorites}
                        favoriteListGuid={event.id ? favoriteListGuidByEventId.get(event.id) : undefined}
                        onRefetchFavoritesNeeded={refetchEventFavorites}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          {isSearching && !isLoadingResults && totalResults === 0 && (
            <div className="flex flex-1 flex-col items-center justify-between pt-30 pb-12">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="flex size-11 items-center justify-center">
                  <Icon name="area-search" width={20} height={20} className="text-jncquoi-neutral-1000" />
                </div>
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
                  {t('App.Events.SearchNoResultsTitle')}
                </p>
                <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[14px] tracking-[0.5px]">
                  {t('App.Events.SearchNoResultsSubtitle')}
                </p>
              </div>
            </div>
          )}
        </div>

        {isSearching && !isLoadingResults && totalResults === 0 && (
          <div className="pb-safe-area shrink-0 px-4 pt-4">
            <Button
              variant="primary-solid"
              palette="dark"
              size="lg"
              className="w-full"
              onClick={handleSearchAgain}
            >
              {t('App.Events.SearchAgain')}
            </Button>
          </div>
        )}
      </div>

      <SoldOutEventSheet
        open={!!soldOutEvent}
        onOpenChange={open => !open && setSoldOutEvent(undefined)}
        onViewAnyway={() => {
          if (soldOutEvent?.id) navigate(`${eventDetailPath}/${soldOutEvent.id}`);
        }}
      />
    </>
  );
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  const needle = query.trim();
  if (!needle) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(needle.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className="font-semibold">{text.slice(idx, idx + needle.length)}</span>
      {text.slice(idx + needle.length)}
    </>
  );
}
